import { Users, MapPin } from "lucide-react";
import { MetricCard, SectionHeading, REGION_LABELS, ngn, usd } from "./ui";

const TopList = ({ title, rows, render, testId }) => (
  <div className="p-5 rounded-xl bg-surface border border-white/10" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-foreground/55 font-medium mb-4">{title}</p>
    {rows.length === 0 ? (
      <p className="text-sm text-foreground/40">No data yet — {title.toLowerCase().includes("views") ? "0 views" : "₦0.00 / $0.00"} so far.</p>
    ) : (
      <ol className="space-y-3">
        {rows.map((f, i) => (
          <li key={f.id} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-terracotta/15 text-terracotta text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{f.title}</p>
              <p className="text-xs text-foreground/45 truncate">{f.filmmaker_name}</p>
            </div>
            <span className="text-sm font-semibold text-gold tabular-nums whitespace-nowrap">{render(f)}</span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export default function ViewerAnalytics({ data }) {
  const d = data || {};
  const regions = d.regions || { local: 0, african: 0, diaspora: 0, unknown: 0 };
  const total = d.total || 0;
  return (
    <section data-testid="viewer-analytics">
      <SectionHeading eyebrow="Audience" title="Viewer analytics" description="Who's watching and what's performing. Viewer country is captured at sign-up and login (backfilled from past rentals)." />
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 mb-6">
        <MetricCard accent label="Registered viewers" value={total.toLocaleString()} icon={Users} tone="text-gold" testId="va-total" />
        <div className="p-5 rounded-xl bg-surface border border-white/10" data-testid="va-regions">
          <p className="text-[11px] uppercase tracking-wider text-foreground/55 font-medium mb-4 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Regional distribution</p>
          <div className="space-y-3">
            {Object.keys(REGION_LABELS).map((k) => {
              const n = regions[k] || 0;
              const pct = total ? Math.round((n / total) * 100) : 0;
              return (
                <div key={k} data-testid={`va-region-${k}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{REGION_LABELS[k]}</span>
                    <span className="tabular-nums text-foreground/70">{n} · {pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className={`h-full rounded-full transition-[width] duration-700 ${k === "unknown" ? "bg-white/20" : "bg-gradient-to-r from-terracotta to-gold"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {(d.top_countries || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {d.top_countries.map((c) => (
                <span key={c.country_code} className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 font-mono">{c.country_code} · {c.count}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TopList title="Top films · ₦ revenue" rows={d.top_films_by_revenue_naira || []} render={(f) => ngn(f.revenue_naira)} testId="va-top-revenue-ngn" />
        <TopList title="Top films · $ revenue" rows={d.top_films_by_revenue_usd || []} render={(f) => usd(f.revenue_usd_cents)} testId="va-top-revenue-usd" />
        <TopList title="Top films · views" rows={d.top_films_by_views || []} render={(f) => `${f.views.toLocaleString()} views`} testId="va-top-views" />
      </div>
    </section>
  );
}
